import DOMPurify from 'dompurify';

/**
 * The image SVG overlay exists only to define clickable areas on the image (an
 * interactive image map). It is injected via dangerouslySetInnerHTML, so it is
 * sanitized here at the render sink with a strict deny-by-default allowlist:
 * only the <svg> hotspot subset (<a> + shape) and the <style> that styles them
 * survive — script, iframe, img, foreignObject, arbitrary markup, on* handlers
 * and javascript: links are removed.
 *
 * DOMPurify here is the browser build (no jsdom): the caller renders the overlay
 * client-side only (after mount), so SSR never ships overlay markup and the
 * server never needs a DOM. (isomorphic-dompurify pulls a jsdom whose
 * html-encoding-sniffer -> @exodus/bytes ESM breaks Next's CommonJS require on
 * SSR.) This mirrors the authoring-side allowlist in the questionnaire builder;
 * keep the two in sync.
 */

// Clickable image-map surface: the SVG hotspot subset + the <style> for the areas.
const ALLOWED_TAGS = ['style', 'svg', 'g', 'a', 'title', 'polygon', 'polyline', 'rect', 'circle', 'ellipse', 'path'];
const ALLOWED_ATTR = [
  // svg (lowercased forms included for DOMPurify's case-insensitive matching)
  'viewBox',
  'viewbox',
  'preserveAspectRatio',
  'preserveaspectratio',
  // shape geometry
  'points',
  'x',
  'y',
  'width',
  'height',
  'rx',
  'ry',
  'cx',
  'cy',
  'r',
  'd',
  // anchors + accessibility
  'href',
  'xlink:href',
  'target',
  'rel',
  'aria-label',
  'id',
  'class',
];

export function sanitizeSvgOverlay(html: string): string {
  if (!html.trim()) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORCE_BODY: true, // keep a leading <style> in <body> (the parser would otherwise hoist it to <head>)
  });
}
